"use client";

import React from "react";
import { usePathname } from "next/navigation";
import Navbar from "./Navbar";
import { MobileBottomNav } from "../../components/shared";

export default function Chrome({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || "";
  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");
  // Auth screens get a distraction-free shell (no primary nav / bottom tab
  // bar) so the sign-in card is the sole focus, matching the premium,
  // focused-auth pattern used by Coinbase/Linear/Stripe-style login pages.
  const isFocusedAuthRoute = pathname === "/login" || pathname.startsWith("/login/");
  const showNavbar = !isAdminRoute && !isFocusedAuthRoute;

  // FIX-58 (mobile "typed a value, then couldn't update or remove"):
  // the on-screen keyboard shrinks the *visual* viewport without shrinking
  // the layout viewport, and on iOS Safari sticky/fixed elements are
  // re-anchored to that visual viewport - so they ride up and sit directly
  // above the keyboard. On a small phone that leaves ~300-370px of visible
  // page, which the profile-edit sticky save bar plus the fixed bottom nav
  // can cover entirely, including the field being typed into and its own
  // remove button. Reproduced at 320x312 / 360x352 / 375x367: input AND
  // remove button both untappable.
  //
  // There is no CSS media feature for "keyboard is open" (a max-height query
  // never fires, because the layout viewport is unchanged), so we detect it
  // from visualViewport and expose it as a root attribute. Pages that pin
  // anything to the bottom un-pin it while [data-kbd-open] is set.
  React.useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // 150px is comfortably above browser-chrome collapse (~60-90px) and
        // well below the shortest on-screen keyboard (~240px). Requiring a
        // focused editable element as well keeps pinch-zoom (which also
        // shrinks the visual viewport) from being mistaken for the keyboard.
        const el = document.activeElement as HTMLElement | null;
        const editing =
          !!el &&
          (el.tagName === "INPUT" ||
            el.tagName === "TEXTAREA" ||
            el.tagName === "SELECT" ||
            el.isContentEditable);
        const open = editing && window.innerHeight - vv.height > 150;
        const root = document.documentElement;
        if (open) root.setAttribute("data-kbd-open", "");
        else root.removeAttribute("data-kbd-open");
      });
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    // focus changes can flip `editing` without any viewport resize (moving
    // from one input straight to the next keeps the keyboard up).
    window.addEventListener("focusin", sync);
    window.addEventListener("focusout", sync);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      window.removeEventListener("focusin", sync);
      window.removeEventListener("focusout", sync);
      document.documentElement.removeAttribute("data-kbd-open");
    };
  }, []);

  return (
    <>
      {showNavbar ? <Navbar /> : null}
      <div
        id="content"
        className={showNavbar ? "site-shell site-shell-with-bottom-nav" : "site-shell"}
        style={{
          // reserve space for the fixed navbar only when it is rendered
          paddingTop: showNavbar ? "var(--site-nav-height, 5rem)" : 0,
        }}
      >
        {children}
      </div>
      {showNavbar ? <MobileBottomNav /> : null}
      <style jsx global>{`
        /* MobileBottomNav.tsx measures its own real height (card + safe-area
           inset) into --site-bottom-nav-height. This used to be a static
           pb-16 (4rem) Tailwind class, which undershot the bar's actual
           rendered height, so page content's last ~40-90px sat underneath
           the floating bottom nav on mobile. */
        @media (max-width: 639.98px) {
          .site-shell-with-bottom-nav {
            padding-bottom: var(--site-bottom-nav-height, 7.5rem);
          }
        }

        /* FIX-58: while the on-screen keyboard is open the bottom nav has no
           job - the user is typing, not navigating - and on iOS it is pinned
           directly above the keyboard where it covers form controls. Hide it
           with visibility (not display) so its ResizeObserver keeps reporting
           the same height and the shell's reserved padding does not collapse,
           which would jerk the page under the caret. */
        html[data-kbd-open] [data-site-bottom-nav] {
          visibility: hidden;
        }

        /* Auto-hide on scroll down (MobileBottomNav sets data-hidden). The bar
           is ~110px of a phone screen and was covering body copy at scroll
           position zero; reserved padding only protects the end of the page.
           Transform keeps the layout box intact so --site-bottom-nav-height
           stays stable and the reserved padding never collapses. */
        [data-site-bottom-nav] {
          transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease;
          will-change: transform;
        }
        [data-site-bottom-nav][data-hidden="true"] {
          transform: translateY(calc(100% + 1rem));
          opacity: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-site-bottom-nav] {
            transition: none;
          }
        }
      `}</style>
    </>
  );
}
